import React, { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AppSumoFormArgs, ErrorType } from '.';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { ErrorMessage } from '@hookform/error-message';
import { FieldError } from '~/App/Components/Forms';
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Input } from './Input';

const MAX_CODE_INPUTS = 5;

export const getCodeInputName = (index: number) =>
  `appsumo_codes[${index}].value`;

export interface AppSumoCodeListProps {
  disabled?: boolean;
  initialCodes?: string[];
  formErrors?: Record<string, string[]>;
  maxCodes?: number;
}

export type FieldArrayType = { value: string }[];

export interface AppSumoCodeListFormArgs
  extends Omit<AppSumoFormArgs, 'appsumo_codes'> {
  appsumo_codes: FieldArrayType;
}

export const mapCodesToStringArray = (codes?: FieldArrayType): string[] => {
  if (!codes) return [];
  return codes.map((code) => {
    return code.value;
  });
};

export function AppSumoCodeList({
  disabled,
  initialCodes = [],
  formErrors,
  maxCodes = MAX_CODE_INPUTS,
}: AppSumoCodeListProps) {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, errors, getValues, setError, control } =
    useFormContext<AppSumoCodeListFormArgs>();
  const { fields, append, remove } = useFieldArray({
    name: 'appsumo_codes',
    control,
  });

  const onAddClick = () => {
    if (disabled) return;
    append({ appsumo_codes: '' });
  };

  const onRemoveClick = (index: number) => {
    if (disabled) return;
    remove(index);
  };

  const duplicateEntryValidation = useCallback(
    (value: string) => {
      const mappedCodes = mapCodesToStringArray(getValues().appsumo_codes);
      if (mappedCodes.filter((code) => code === value).length > 1) {
        return 'This is a duplicate code';
      }

      return true;
    },
    [getValues]
  );

  // Append default values and if has error, includes the errors aswell
  useEffect(() => {
    initialCodes.forEach((code, index) => {
      append({ appsumo_codes: code });

      const error = formErrors?.['appsumo_codes']?.[0]?.[code] as
        | string
        | undefined;

      if (error) {
        setError(getCodeInputName(index), {
          type: ErrorType.Manual,
          message: error,
        });
      }
    });
  }, [initialCodes]);

  // If there are no initial codes, trigger the onAddClick to add 1 code field.
  useEffect(() => {
    if (initialCodes.length === 0 && fields.length === 0) {
      onAddClick();
    }
  }, [fields, initialCodes]);

  return (
    <div className='flex flex-col gap-y-4'>
      <span className='text-base font-bold v2-text-neutral-900'>
        AppSumo code
      </span>
      {fields.map((field, index) => {
        const maxReached = maxCodes === fields.length;
        const lastItem = fields.length === index + 1;
        const hideDelete = fields.length === 1 || (!maxReached && lastItem);
        const hideAdd = !lastItem || maxCodes === fields.length;
        const inputName = getCodeInputName(index);

        return (
          <div className='flex flex-col gap-y-1.5 w-full' key={field.id}>
            <div className='inline-flex gap-x-5 items-center'>
              <Input
                key={field.id}
                name={inputName}
                placeholder='Enter code'
                inputRef={register({
                  required: 'Code is required',
                  validate: {
                    duplicateEntryValidation,
                  },
                })}
                defaultValue={field.appsumo_codes as string}
              />
              {!hideDelete && (
                <FontAwesomeIcon
                  onClick={() => onRemoveClick(index)}
                  className={cx(
                    'text-xl text-neutral-300 mx-2.5 hover:v2-text-orange-700',
                    {
                      'cursor-pointer': !disabled,
                      'cursor-not-allowed': disabled,
                    }
                  )}
                  icon={faTrash}
                />
              )}
              {!hideAdd && (
                <Button
                  variant={ButtonVariant.Primary}
                  className='pt-[.3rem] pb-1 px-2'
                  onClick={onAddClick}
                  disabled={disabled}
                >
                  <FontAwesomeIcon className='text-[23px]' icon={faPlus} />
                </Button>
              )}
            </div>
            <ErrorMessage
              className='!text-sm !v2-text-orange-600 v2-animate-fade-in'
              name={inputName}
              errors={errors}
              as={FieldError}
            />
          </div>
        );
      })}
    </div>
  );
}
